import { openGlobalDialog } from "../actions/globalEditDialogActions";
import {
	TRASH_LIFECYCLE_STATUS,
	ARCHIVE_LIFECYCLE_STATUS,
} from "../utilities/lifecycleStatus";
import { createNewFolder, changeFolderName } from "../actions/documentActions";

/**
 * Validates folder name and returns validation result
 * @param {string} folderName - The folder name to validate
 * @param {Array} existingFolders - Array of existing folders to check for duplicates
 * @param {Object} options - Optional validation parameters
 * @param {string} options.currentName - Current name (for edit operations)
 * @param {number} options.excludeFolderID - Folder ID to exclude from duplicate check (for edit operations)
 * @returns {Object} { isValid: boolean, errorMessage: string }
 */
function validateFolderName(folderName, existingFolders, options = {}) {
	const { currentName, excludeFolderID } = options;

	// Check if folder name is empty
	const isEmpty = folderName.trim() === "";
	if (isEmpty) {
		return {
			isValid: false,
			errorMessage: "Folder names cannot be empty",
		};
	}

	// Check character limit
	const exceedsCharLimit = folderName.length > 255;
	if (exceedsCharLimit) {
		return {
			isValid: false,
			errorMessage: "Folder names cannot exceed 255 characters",
		};
	}

	// Check for illegal characters
	const hasIllegalCharacters = folderName.match(/[<>:"/\\|?*%#]/);
	if (hasIllegalCharacters) {
		return {
			isValid: false,
			errorMessage:
				'Folder names cannot contain any of the following characters: < > : " / \\ | ? * % #',
		};
	}

	// Check for reserved directory names
	const isReservedName = folderName.match(/^\.{1,2}$/);
	if (isReservedName) {
		return {
			isValid: false,
			errorMessage: "Folder name cannot be . or ..",
		};
	}

	// Check for duplicate names
	const duplicateFolders = existingFolders
		.filter((item) => item.type === "Folder")
		.filter((item) => {
			const nameMatches =
				item.filename.toUpperCase().trim() ===
				folderName.toUpperCase().trim();
			const isNotExcluded =
				!excludeFolderID || item.id !== excludeFolderID;
			return nameMatches && isNotExcluded;
		});

	if (duplicateFolders.length > 0) {
		const hasDeletedMatch = duplicateFolders.some(
			(folder) =>
				folder.folderLifecycleStatus === TRASH_LIFECYCLE_STATUS ||
				folder.folderLifecycleStatus === ARCHIVE_LIFECYCLE_STATUS
		);
		const folderType = hasDeletedMatch
			? "(in the trash bin or archive) "
			: "";
		return {
			isValid: false,
			errorMessage: `There is already a folder ${folderType}in the current directory with this name`,
		};
	}

	// Check if name is different (for edit operations)
	if (currentName !== undefined && folderName === currentName) {
		return {
			isValid: false,
			errorMessage: "Please enter a different folder name",
		};
	}

	return {
		isValid: true,
		errorMessage: "",
	};
}

function showNewFolderDialog({
	matterID,
	apiBasepath,
	currentLocationFilesAndFolders,
}) {
	return function (dispatch) {
		openGlobalDialog(
			{
				label: "Create New Folder",
				placeHolder: "New folder name",
				submitButtonLabel: "Create Folder",
			},

			(state) => {
				createNewFolder(
					matterID,
					"/" + apiBasepath,
					state.value
				)(dispatch);
			},
			(state) => {
				const validation = validateFolderName(
					state.value,
					currentLocationFilesAndFolders
				);
				return {
					canSubmit: validation.isValid,
					errorMessage: validation.errorMessage,
				};
			}
		)(dispatch);
	};
}

function showEditFolderDialog({
	folderID,
	matterID,
	currentName,
	apiBasepath,
	currentLocationFilesAndFolders,
	onSuccess,
}) {
	return function (dispatch) {
		openGlobalDialog(
			{
				label: "Change Folder Name",
				value: currentName,
				submitButtonLabel: "Rename Folder",
			},
			(state) => {
				changeFolderName(
					matterID,
					folderID,
					state.value,
					apiBasepath,
					onSuccess
				)(dispatch);
			},
			(state) => {
				state.value = state.value.trim();
				const validation = validateFolderName(
					state.value,
					currentLocationFilesAndFolders,
					{
						currentName: currentName,
						excludeFolderID: folderID,
					}
				);
				return {
					canSubmit: validation.isValid,
					errorMessage: validation.errorMessage,
				};
			}
		)(dispatch);
	};
}

export { showNewFolderDialog, showEditFolderDialog, validateFolderName };
